'use client';

import { useContext, useState, useCallback, createContext } from 'react';
import type {
  BulkUploadProcessorContextType,
  BulkUploadProcessorProviderProps,
} from './BulkUploadProcessorProvider.types';

const BulkUploadProcessorContext =
  createContext<BulkUploadProcessorContextType>({
    activePageFilter: null,
    setActivePageFilter: () => {},
    togglePageFilter: () => {},
    selectedDocChipPages: {},
    setSelectedDocChipPages: () => {},
    clearDocChipSelection: () => {},
    selectedUnclassifiedPages: [],
    setSelectedUnclassifiedPages: () => {},
    clearUnclassifiedSelection: () => {},
    clearAllSelections: () => {},
  });

export const useBulkUploadProcessor = () => {
  const context = useContext(BulkUploadProcessorContext);
  if (!context) {
    throw new Error(
      'useBulkUploadProcessor must be used within a BulkUploadProcessorProvider',
    );
  }
  return context;
};

const BulkUploadProcessorProvider = ({
  children,
}: BulkUploadProcessorProviderProps) => {
  const [activePageFilter, setActivePageFilterState] = useState<
    number | '__unclassified__' | null
  >(null);
  const [selectedDocChipPages, setSelectedDocChipPagesState] = useState<
    Record<number, number[]>
  >({});
  const [selectedUnclassifiedPages, setSelectedUnclassifiedPagesState] =
    useState<number[]>([]);

  const setActivePageFilter = useCallback(
    (filter: number | '__unclassified__' | null) => {
      setActivePageFilterState(filter);
    },
    [],
  );

  const togglePageFilter = useCallback(
    (filter: number | '__unclassified__' | null) => {
      setActivePageFilterState((prev) => (prev === filter ? null : filter));
    },
    [],
  );

  // Document chip selection actions
  const setSelectedDocChipPages = useCallback(
    (docId: number, pages: number[]) => {
      setSelectedDocChipPagesState((prev) => ({
        ...prev,
        [docId]: pages,
      }));
    },
    [],
  );

  const clearDocChipSelection = useCallback((docId?: number) => {
    if (docId) {
      setSelectedDocChipPagesState((prev) => {
        const next = { ...prev };
        delete next[docId];
        return next;
      });
    } else {
      setSelectedDocChipPagesState({});
    }
  }, []);

  // Unclassified selection actions
  const setSelectedUnclassifiedPages = useCallback((pages: number[]) => {
    setSelectedUnclassifiedPagesState(pages);
  }, []);

  const clearUnclassifiedSelection = useCallback(() => {
    setSelectedUnclassifiedPagesState([]);
  }, []);

  // Global selection clearing
  const clearAllSelections = useCallback(() => {
    setSelectedDocChipPagesState({});
    setSelectedUnclassifiedPagesState([]);
  }, []);

  return (
    <BulkUploadProcessorContext.Provider
      value={{
        activePageFilter,
        setActivePageFilter,
        togglePageFilter,
        selectedDocChipPages,
        setSelectedDocChipPages,
        clearDocChipSelection,
        selectedUnclassifiedPages,
        setSelectedUnclassifiedPages,
        clearUnclassifiedSelection,
        clearAllSelections,
      }}
    >
      {children}
    </BulkUploadProcessorContext.Provider>
  );
};

export { BulkUploadProcessorProvider, BulkUploadProcessorContext };
